import {Alert,Button,Divider,Form,Input,List,Modal,Popconfirm,Select,Spin,Space,Typography} from "antd";
import {UserAddOutlined,QrcodeOutlined} from "@ant-design/icons";
import {useState,useContext,useEffect} from "react";
import {useTranslation} from "react-i18next";
import QRCode from "react-qr-code";
import {AuthContext} from "../../context/AuthContext";
import {useGlobalToast} from "../../context/ToastProvider";
import {db,functions} from "../../firebase";
import {httpsCallable} from "firebase/functions";
import {collection,onSnapshot,query,where} from "firebase/firestore";
import {isManagerRole} from "../../auth/fleetRoles";
export default function RegisterDriver(){
const [open,setOpen]=useState(false);
  const [form] = Form.useForm();
  const [countryCode, setCountryCode] = useState("+34");
  const [mobileNumber, setMobileNumber] = useState("");
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [driverJoinQrValue, setDriverJoinQrValue] = useState("");
  const [driverJoinQrLoading, setDriverJoinQrLoading] = useState(false);
  const [savingDriver, setSavingDriver] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [removingInvitePhone, setRemovingInvitePhone] = useState("");
  const { getFleetID, fleetRole } = useContext(AuthContext);
  const fleetId = getFleetID();
  const toast = useGlobalToast();
  const { t } = useTranslation();

  const getTranslatedMessage = (messageKey, fallbackMessage) => {
    if (messageKey) {
      const translated = t(messageKey);
      if (translated && translated !== messageKey) {
        return translated;
      }
    }
    return fallbackMessage || t("feedback.unavailable");
  };

  const canManageUsers = isManagerRole(fleetRole);
  useEffect(() => {
    if (!qrModalOpen || !fleetId || !canManageUsers) {
      setDriverJoinQrValue("");
      setDriverJoinQrLoading(false);
      return undefined;
    }

    let cancelled = false;
    const callable = httpsCallable(functions, "create_driver_join_token");

    setDriverJoinQrLoading(true);
    callable({ fleetId })
      .then((response) => {
        if (cancelled) return;
        const qrValue = String(response?.data?.data?.qrValue || "").trim();
        setDriverJoinQrValue(qrValue);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error creating driver join QR:", error);
        toast({
          title: t("setup.driverQrGenerationError", {
            defaultValue: "Driver QR could not be generated.",
          }),
          status: "error",
        });
        setDriverJoinQrValue("");
      })
      .finally(() => {
        if (!cancelled) {
          setDriverJoinQrLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [qrModalOpen, fleetId, canManageUsers, toast, t]);

  useEffect(() => {
    if (!fleetId || !canManageUsers || !open) return undefined;
    setLoadingInvites(true);

    const invitesRef = collection(db, "invites");
    const invitesQuery = query(invitesRef, where("fleetId", "==", fleetId));

    const unsubscribe = onSnapshot(
      invitesQuery,
      (snapshot) => {
        const docs = snapshot.docs
          .map((inviteDoc) => {
            const data = inviteDoc.data() || {};
            return {
              id: inviteDoc.id,
              phoneNumber: data.phoneNumber || inviteDoc.id,
              status: String(data.status || "").toUpperCase(),
              createdAt: data.createdAt || null,
              sms: data.sms || {},
            };
          })
          .filter((invite) => invite.status === "PENDING")
          .sort((a, b) => {
            const aMillis = a.createdAt?.toMillis?.() || 0;
            const bMillis = b.createdAt?.toMillis?.() || 0;
            return bMillis - aMillis;
          });

        setPendingInvites(docs);
        setLoadingInvites(false);
      },
      (error) => {
        console.error("Error fetching pending invites:", error);
        setPendingInvites([]);
        setLoadingInvites(false);
      }
    );

    return () => unsubscribe();
  }, [fleetId,canManageUsers,open]);

  const countryCodes = [
    { code: "+1", name: "USA" },
    { code: "+44", name: "UK" },
    { code: "+33", name: "FR" },
    { code: "+49", name: "DE" },
    { code: "+34", name: "ES" },
    { code: "+39", name: "IT" },
    { code: "+31", name: "NL" },
    { code: "+351", name: "PT" },
    { code: "+376", name: "AD" },
    { code: "+41", name: "CH" },
    { code: "+32", name: "BE" },
    { code: "+352", name: "LU" },
    { code: "+48", name: "PL" },
    { code: "+43", name: "AT" },
  ];

  const addDriver = async () => {
    if (!mobileNumber?.trim()) {
      toast({
        title: t("toast.missingPhoneNumber", {
          defaultValue: "Enter a valid phone number before registering the driver.",
        }),
        status: "warning",
      });
      return;
    }

    try {
      setSavingDriver(true);
      const fullPhoneNumber = `${countryCode}${mobileNumber}`;
      const registerDriver = httpsCallable(functions, "register_driver");
      const response = await registerDriver({
        fleetId: getFleetID(),
        phoneNumber: fullPhoneNumber,
      });
      const payload = response?.data?.data || {};

      setRegisterModalOpen(false);
      setMobileNumber("");
      form.resetFields();

      const status = String(payload?.status || "").toUpperCase();
      const smsSent = payload?.sms?.sent === true;
      const successDescription =
        status === "DRIVER_ADDED"
          ? t("toast.successNewDriver")
          : status === "INVITE_UPDATED"
          ? t("toast.successInviteUpdated")
          : t("toast.successInviteCreated");

      toast({
        title: successDescription,
        status: "success",
        duration: 9000,
        isClosable: true,
        position: "bottom-right",
        containerStyle: { paddingRight: "15px" },
      });

      if ((status === "INVITE_CREATED" || status === "INVITE_UPDATED") && !smsSent) {
        toast({
          title: t("toast.inviteSmsNotSent"),
          status: "warning",
        });
      }
    } catch (error) {
      const messageKey = error?.details?.messageKey || "toast.errorNewDriver";
      toast({ error,
        title: getTranslatedMessage(
          messageKey,
          t("toast.errorNewDriver", { defaultValue: "Driver could not be registered." })
        ),
        status: "warning",
        duration: 9000,
        isClosable: true,
        position: "bottom-right",
        containerStyle: { paddingRight: "15px" },
      });
      console.log("Error creating driver:", error);
    } finally {
      setSavingDriver(false);
    }
  };

  const removeInvite = async (phoneNumber) => {
    try {
      setRemovingInvitePhone(phoneNumber);
      const removeDriverInvite = httpsCallable(functions, "remove_driver_invite");
      await removeDriverInvite({
        fleetId,
        phoneNumber,
      });
      toast({
        title: t("toast.successInviteRemoved"),
        status: "success",
      });
    } catch (error) {
      const messageKey = error?.details?.messageKey || "toast.errorRemovingInvite";
      toast({ error,
        title: getTranslatedMessage(messageKey, t("toast.errorRemovingInvite")),
        status: "warning",
      });
      console.error("Error removing invite:", error);
    } finally {
      setRemovingInvitePhone("");
    }
  };


if(!canManageUsers)return null;
return <>
<Button shape="round" icon={<UserAddOutlined/>} title={t("setup.registerDriver")} aria-label={t("setup.registerDriver")} onClick={()=>setOpen(true)} />
<Modal open={open} title={t("setup.registerDriver")} onCancel={()=>setOpen(false)} footer={null}>              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Alert
                  showIcon
                  type="info"
                  message={t("setup.driverOnboardingHint", {
                    defaultValue: "Add a driver manually or share your fleet QR code for quick onboarding.",
                  })}
                />
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<UserAddOutlined />}
                    onClick={() => setRegisterModalOpen(true)}
                  >
                    {t("setup.newdriver")}
                  </Button>
                  <Button icon={<QrcodeOutlined />} onClick={() => setQrModalOpen(true)}>
                    {t("setup.registerDriver")}
                  </Button>
                </Space>
                <Divider style={{ margin: "4px 0" }} />
                <Typography.Text strong>
                  {t("setup.pendingInvitesTitle")}
                </Typography.Text>
                <Typography.Text type="secondary">
                  {t("setup.pendingInvitesHelp")}
                </Typography.Text>
                <List
                  loading={loadingInvites}
                  locale={{
                    emptyText: t("setup.noPendingInvites"),
                  }}
                  dataSource={pendingInvites}
                  renderItem={(invite) => {
                    const createdAt =
                      invite.createdAt?.toDate?.() instanceof Date
                        ? invite.createdAt.toDate()
                        : null;
                    return (
                      <List.Item
                        actions={[
                          <Popconfirm
                            key={`remove-${invite.phoneNumber}`}
                            title={t("setup.removeInvite")}
                            okText={t("setup.removeInvite")}
                            cancelText={t("setup.cancel", { defaultValue: "Cancel" })}
                            onConfirm={() => removeInvite(invite.phoneNumber)}
                          >
                            <Button
                              size="small"
                              danger
                              loading={removingInvitePhone === invite.phoneNumber}
                            >
                              {t("setup.removeInvite")}
                            </Button>
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          title={invite.phoneNumber}
                          description={
                            createdAt
                              ? `${t("setup.inviteCreatedAt")}: ${createdAt.toLocaleString()}`
                              : t("setup.inviteCreatedAt")
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              </Space>
</Modal>
      <Modal
        open={registerModalOpen}
        title={t("setup.newdriver")}
        okText={t("setup.send")}
        cancelText={t("setup.cancel", { defaultValue: "Cancel" })}
        confirmLoading={savingDriver}
        okButtonProps={{ disabled: !mobileNumber?.trim() }}
        onOk={addDriver}
        onCancel={() => {
          setRegisterModalOpen(false);
          setMobileNumber("");
          form.resetFields();
        }}
      >
        <Form layout="vertical" form={form}>
          <Form.Item label={t("setup.countryCode")} required>
            <Select
              value={countryCode}
              onChange={(value) => setCountryCode(value)}
              options={countryCodes.map((country) => ({
                value: country.code,
                label: `${country.name} (${country.code})`,
              }))}
            />
          </Form.Item>
          <Form.Item label={t("setup.mobile")} required>
            <Input
              value={mobileNumber}
              onChange={(event) => setMobileNumber(event.target.value)}
              placeholder={t("setup.mobile")}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={qrModalOpen}
        title={t("setup.registerDriver")}
        footer={null}
        onCancel={() => setQrModalOpen(false)}
      >
        <div className="setup-qr-wrapper">
          {driverJoinQrLoading ? (
            <Spin />
          ) : driverJoinQrValue ? (
            <QRCode style={{ height: "auto", maxWidth: "100%", width: "100%" }} value={driverJoinQrValue} />
          ) : (
            <Alert
              type="error"
              message={t("setup.driverQrGenerationError", { defaultValue: "Driver QR could not be generated." })}
              showIcon
            />
          )}
        </div>
      </Modal>
</>;
}
